type RegisterAppOptions = {
  appId: string
}

let nativeWechat: typeof import('expo-native-wechat') | undefined

try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  nativeWechat = require('expo-native-wechat')
} catch {
  console.log('wechat sdk not available')
}

async function isSuccess(rt: Promise<boolean> | undefined): Promise<boolean> {
  const result = await rt
  if (!result || typeof result !== 'object' || !('id' in result) || !('success' in result)) return !!result

  const objResult = result as { id: string; success: boolean }
  return objResult.success
}

export async function registerApp(options: RegisterAppOptions) {
  if (!nativeWechat) return false

  return isSuccess(
    nativeWechat.registerApp({
      appid: options.appId,
      universalLink: 'https://jinshuju.net/',
      log: true,
      logPrefix: 'nextapp-wechat',
    })
  )
}

export async function isWechatAvailable() {
  if (!nativeWechat) return false
  return isSuccess(nativeWechat.isWechatInstalled())
}

export enum WechatScene {
  Session = nativeWechat ? nativeWechat.NativeWechatConstants.WXSceneSession : 0,
  Timeline = nativeWechat ? nativeWechat.NativeWechatConstants.WXSceneTimeline : 0,
  Favorite = nativeWechat ? nativeWechat.NativeWechatConstants.WXSceneFavorite : 0,
}

type ShareWebpageOptions = {
  title: string
  description: string
  webpageUrl: string
  coverUrl?: string
  scene: WechatScene
}

export type ShareWebpageResult =
  | { success: true }
  | {
      success: false
      error: 'unavailable' | 'failed'
      message?: Maybe<string>
    }

// WeChat WXMediaMessage.checkArgs(): title<=512, description<=1024 (String.length()).
// Over the limit and sendReq() silently returns false. Leave headroom.
const WECHAT_TITLE_MAX = 300
const WECHAT_DESCRIPTION_MAX = 800

export async function shareWebpage(options: ShareWebpageOptions): Promise<ShareWebpageResult> {
  if (!nativeWechat) return { success: false, error: 'unavailable' }

  const resp = (await nativeWechat.shareWebpage({
    title: options.title.slice(0, WECHAT_TITLE_MAX),
    description: options.description.slice(0, WECHAT_DESCRIPTION_MAX),
    webpageUrl: options.webpageUrl,
    coverUrl: options.coverUrl,
    scene: options.scene,
  })) as unknown as { id?: string; success?: boolean; message?: Maybe<string> } | boolean | undefined

  if (resp === true) return { success: true }
  if (resp && typeof resp === 'object' && resp.success) return { success: true }

  const message = resp && typeof resp === 'object' ? resp.message : undefined
  return { success: false, error: 'failed', message }
}

type SendAuthRequestArgs = {
  scope?: string
  state?: string
}

type SendAuthRequestData = {
  code: string
  state?: string
}

export type SendAuthRequestResult =
  | {
      success: false
      error: 'unavailable' | 'wechat_not_installed' | 'auth_failed'
      message?: Maybe<string>
    }
  | {
      success: true
      data: SendAuthRequestData
    }

export async function sendAuthRequest(args?: SendAuthRequestArgs): Promise<SendAuthRequestResult> {
  if (!nativeWechat) return { success: false, error: 'unavailable' }

  const wechatInstalled = await isSuccess(nativeWechat.isWechatInstalled())
  if (!wechatInstalled) {
    return { success: false, error: 'wechat_not_installed' }
  }

  const resp = await nativeWechat.sendAuthRequest({ scope: args?.scope ?? 'snsapi_userinfo', state: args?.state })
  if (resp && resp.errorCode !== 0) {
    return { success: false, error: 'auth_failed', message: resp.errorStr }
  }

  return { success: true, data: { code: resp.data.code, state: resp.data.state } }
}
